import { action, observable, runInAction } from "mobx";
import { ProgrammeUpdatesDocumentsModel, ProgrammeUpdatesDocumentsDTO, ProgrammeUpdatesDocumentTypeDTO } from "./ProgrammeUpdatesDocumentsModel";
import { ApiResult, FieldType, ViewModelBase } from "@shoothill/core";
import { AppUrls } from "AppUrls";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";

export class ProgrammeUpdatesDocumentsViewModel extends ViewModelBase<ProgrammeUpdatesDocumentsModel> {
    @observable programmeUpdatesDocuments: ProgrammeUpdatesDocumentsModel[] = [];
    @observable hasProgrammeUpdatesDocumentsChanged: boolean = false;
    @observable selectedProgrammeUpdateDocumentType: ProgrammeUpdatesDocumentTypeDTO | null = null;
    @observable programmeUpdateDocumentTypes: ProgrammeUpdatesDocumentTypeDTO[] = [];

    // Method to set document types from API
    @action
    public setProgrammeUpdateDocumentTypes = (types: ProgrammeUpdatesDocumentTypeDTO[]) => {
        this.programmeUpdateDocumentTypes = types;
    };

    @action
    public setSelectedProgrammeUpdateDocumentType = (type: ProgrammeUpdatesDocumentTypeDTO | null) => {
        this.selectedProgrammeUpdateDocumentType = type;
    };

    projectId: string;
    date: Date;
    public server: ServerViewModel = new ServerViewModel();

    constructor(projectId: string, date: Date, model = new ProgrammeUpdatesDocumentsModel()) {
        super(model);
        this.setDecorators(ProgrammeUpdatesDocumentsModel);
        this.projectId = projectId;
        this.date = date;
    }

    @action
    public reset = () => {
        this.programmeUpdatesDocuments = [];
        this.hasProgrammeUpdatesDocumentsChanged = false;
        this.setIsLoading(false);
    };

    @action
    public fromDto = (dtos: ProgrammeUpdatesDocumentsDTO[]) => {
        runInAction(() => {
            this.programmeUpdatesDocuments = dtos.map((dto) => {
                const doc = new ProgrammeUpdatesDocumentsModel();
                doc.fromDto(dto);
                return doc;
            });
        });
    };

    @action
    public handleDeleteDocument = (id: string | null): void => {
        if (id !== null) {
            for (let i = 0; i < this.programmeUpdatesDocuments.length; i++) {
                if (this.programmeUpdatesDocuments[i].id === id) {
                    this.hasProgrammeUpdatesDocumentsChanged = true;
                    this.programmeUpdatesDocuments[i].isDeleted = true;
                }
            }
        }
    };

    @action
    public fileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        if (event.target.files !== null && event.target.value !== null && event.target.files.length > 0) {
            if (!this.selectedProgrammeUpdateDocumentType) {
                return;
            }
            let data: any = {
                fileName: event.target.files[0].name,
                formFile: event.target.files[0],
            };
            event.target.value = "";
            const apiResult = await this.fileUpload(data);
            if (apiResult && apiResult.wasSuccessful) {
                let fileToDisplay: ProgrammeUpdatesDocumentsModel = new ProgrammeUpdatesDocumentsModel();
                fileToDisplay.id = Math.random().toString(16).substr(2, 8);
                fileToDisplay.fileName = data.fileName;
                fileToDisplay.url = apiResult.payload;
                fileToDisplay.isDeleted = false;
                fileToDisplay.canDeleteDocument = true;
                fileToDisplay.projectId = this.projectId;
                fileToDisplay.date = this.date.toISOString();
                fileToDisplay.programmeUpdatesDate = this.date.toISOString();
                fileToDisplay.programmeUpdateDocumentTypeId = this.selectedProgrammeUpdateDocumentType.id;
                runInAction(() => {
                    this.programmeUpdatesDocuments.push(fileToDisplay);
                    this.hasProgrammeUpdatesDocumentsChanged = true;
                });
            }
        }
    };

    public fileUpload = async (data: any): Promise<ApiResult<any>> => {
        this.setIsLoading(true);
        const formData = new FormData();
        formData.append("formFile", data.formFile);
        formData.append("fileName", data.fileName);
        const apiResult = await this.Post<any>(AppUrls.Server.File.UploadFile, formData);
        if (apiResult) {
            if (!apiResult.wasSuccessful) {
                // handle error (snackbar, etc.)
            }
        }
        this.setIsLoading(false);
        return apiResult;
    };

    public DownloadFile = async (fileUrl: string, fileName: string): Promise<void> => {
        try {
            const theUrl: URL = new URL(fileUrl);
            const cleanUrl: string = theUrl.origin + theUrl.pathname;

            const apiResult = await this.Post<Blob>(AppUrls.Server.File.DownloadFile, cleanUrl, undefined, { responseType: "blob" });
            const response = apiResult as any;
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
        } catch (exception) {
            console.error(exception);
            this.setIsErrored(true);
        }
    };

    public OpenFile = (fileUrl: string) => {
        window.open(fileUrl, "_blank", "noopener,noreferrer");
    };

    @action
    public upsert = async (): Promise<ApiResult<any>> => {
        this.setIsLoading(true);
        // Prepare DTOs for saving
        const dtos: ProgrammeUpdatesDocumentsDTO[] = this.programmeUpdatesDocuments.map((d) => ({
            id: d.id,
            projectId: d.projectId,
            date: d.date,
            programmeUpdatesDate: d.programmeUpdatesDate,
            fileName: d.fileName,
            url: d.url,
            urlInline: "",
            isDeleted: d.isDeleted,
            createdByUserId: d.createdByUserId,
            createdDate: d.createdDate,
            programmeUpdateDocumentTypeId: d.programmeUpdateDocumentTypeId || this.selectedProgrammeUpdateDocumentType?.id,
        }));

        runInAction(() => {
            // Remove the temp ids before upserting.
            for (let i = 0; i < dtos.length; i++) {
                if (dtos[i].id !== null && dtos[i].id!.length < 10) {
                    dtos[i].id = null;
                }
            }
        });

        const upsertProgrammeUpdateDocuments = {
            programmeUpdateDocuments: dtos.filter((d) => !(d.id === null && d.isDeleted === true)),
        };

        const apiResult = await this.Post<any>(AppUrls.Server.Projects.Construction.ProgrammeUpdates.UpsertProgrammeUpdateDocuments, upsertProgrammeUpdateDocuments);
        if (apiResult) {
            runInAction(() => {
                this.hasProgrammeUpdatesDocumentsChanged = false;
                this.setSelectedProgrammeUpdateDocumentType(null);
            });

            if (!apiResult.wasSuccessful) {
                console.log("Failed to save documents.");
            }
        }

        this.setIsLoading(false);
        return apiResult;
    };

    public async isFieldValid(fieldName: keyof FieldType<ProgrammeUpdatesDocumentsModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
